import { useCallback } from 'react';
import { useForm as useFormBase } from 'react-hook-form';

import noop from 'lodash/noop';

import { yupResolver } from '@hookform/resolvers/yup';

import { is400 } from '@src/corelib/utils/httpUtils';
import { isPromise } from '@src/corelib/utils/validationUtils';

import parseServerErrors from './parseServerErrors';

function getErrorResponse(error) {
  const responseData = error.response.data;
  return 'payload' in responseData ? responseData.payload : responseData;
}

function useForm(params) {
  const { schema, defaultValues, showServerErrors = true, ...restParams } = params || {};

  const {
    setError,
    handleSubmit: handleSubmitBase,
    setValue,
    reset,
    ...form
  } = useFormBase({
    ...restParams,
    defaultValues,
    resolver: yupResolver(schema, { form: { defaultValues } }),
  });

  const setErrors = useCallback(
    (data, nonFieldError = () => {}) => {
      try {
        parseServerErrors(schema, data, setError, nonFieldError);
      }
      catch (error) {
        console.error('Unable not parse errors.', error);
      }
    },
    [schema, setError],
  );

  const handleSubmit = useCallback(
    ({ onSubmit = noop, onSuccess = noop, onError = noop, onInvalid = noop }) => {
      return handleSubmitBase((values) => {
        const promise = onSubmit(values);
        if (isPromise(promise)) {
          return new Promise((resolve) => {
            promise
              .then((response) => {
                onSuccess(response);
                resolve();
              })
              .catch((error) => {
                if (is400(error?.response)) {
                  if (showServerErrors) {
                    setErrors(getErrorResponse(error));
                  }
                  onError(error, { isInvalid: true });
                }
                else {
                  console.error('Uexpected form error.', error);
                  onError(error, { isInvalid: false });
                }
                resolve();
              });
          });
        }

        console.log('Non Promise result. Form submitting attributes may not working.');
        return Promise.resolve();
      }, onInvalid);
    },
    [handleSubmitBase, setErrors, showServerErrors],
  );

  return {
    setError,
    setErrors,
    handleSubmitBase,
    handleSubmit,
    reset,
    setValue,
    ...form,
  };
}

export default useForm;
